import React, { useCallback, useState, lazy, Suspense } from 'react';
import { Icon } from '../../../pages/DesignSystem';
import toast from 'react-hot-toast';
import DayPicker from './DayPicker';
import PlanningTimeline from './PlanningTimeline';
import PlanningWeekGrid from './PlanningWeekGrid';
import AddOccurrenceModal from '../EventList/AddOccurrenceModal';
import { usePlanningView } from '../../../hooks/usePlanningView';
import { useOrganizerEventsStore } from '../../../stores/organizerEventsStore';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import useEventActions from '../../../hooks/useEventActions';
import { Event } from '../../../types/event.types';
import type { EventSeries } from '../../../types/eventSeries.types';
import { locationCache } from '../../../services/LocationCacheService';
import stripeService from '../../../supabase/stripe.service';
import { supabaseEventService } from '../../../supabase/event.service';
import { smartListenerService } from '../../../supabase/SmartListenerService';
import './PlanningView.css';

// Lazy load heavy modals
const InstaPostGenerator = lazy(() => import('../InstaPostGenerator/InstaPostGenerator'));

interface PlanningViewProps {
  events: Event[];
  eventsLoading: boolean;
  onEventSelect: (event: Event) => void;
  onDuplicate: (duplicatedEvent: Partial<Event>) => void;
  onCreateEvent: () => void;
  onCreateEventAt?: (datetime: Date) => void;
  onSwitchToDrafts: () => void;
  variant?: 'mobile' | 'desktop';
}

const toDayKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PlanningView: React.FC<PlanningViewProps> = ({
  events,
  eventsLoading,
  onEventSelect,
  onDuplicate,
  onCreateEvent,
  onCreateEventAt,
  onSwitchToDrafts,
  variant = 'mobile',
}) => {
  const {
    selectedDate,
    setSelectedDate,
    goToNextDay,
    goToPreviousDay,
    goToNextWeek,
    goToPreviousWeek,
    eventsByDay,
    smartRange,
    smartRangeEventsByDay,
    draftsWithoutDate,
    totalEventsThisWeek,
    currentWeek,
    isLoading,
  } = usePlanningView(events, eventsLoading);

  const { organizerProfile } = useOrganizer();

  // Metrics from Zustand store
  const eventMetrics = useOrganizerEventsStore(state => state.eventMetrics);
  const seriesMetrics = useOrganizerEventsStore(state => state.seriesMetrics);
  const metricsLoading = useOrganizerEventsStore(state => state.metricsLoading);

  // Event actions (publish, cancel, delete, restore...)
  const { cancelEvent, restoreEvent, deleteEvent, cancelSeries, restoreSeries } = useEventActions(
    (msg) => toast[msg.type === 'success' ? 'success' : 'error'](msg.text)
  );

  // Instagram modal state
  const [instaEvent, setInstaEvent] = useState<Event | null>(null);

  // Add occurrence modal state
  const [showAddOccurrence, setShowAddOccurrence] = useState(false);
  const [addOccurrenceSeries, setAddOccurrenceSeries] = useState<EventSeries | null>(null);

  const selectedDateKey = toDayKey(selectedDate);

  const handleDuplicate = useCallback((event: Event) => {
    const { id, ...rest } = event;
    onDuplicate({ ...rest, status: 'draft' } as Partial<Event>);
  }, [onDuplicate]);

  // Publish: validate required fields then call stripe service
  const handlePublish = useCallback(async (event: Event) => {
    const missing: string[] = [];
    if (!event.title) missing.push('title');
    if (!event.datetime) missing.push('date');
    if (!event.location && !event.locationName) missing.push('location');
    if (missing.length > 0) {
      toast.error(`Missing required fields: ${missing.join(', ')}. Edit the event first.`);
      return;
    }
    try {
      const result = await stripeService.publishEvent(event.id);
      if (result.success) {
        toast.success('Event published!');
        window.dispatchEvent(new CustomEvent('eventPublished', { detail: { eventId: event.id } }));
      } else {
        toast.error(result.error || `Publish limit reached (${result.eventsUsed || 0}/${result.eventsLimit || 0})`);
      }
    } catch {
      toast.error('Failed to publish event');
    }
  }, []);

  const handleDelete = useCallback(async (event: Event) => {
    await deleteEvent(event.id);
  }, [deleteEvent]);

  const handleCancel = useCallback(async (event: Event) => {
    if (event.seriesId) {
      await cancelSeries(event.seriesId);
    } else {
      await cancelEvent(event.id);
    }
  }, [cancelEvent, cancelSeries]);

  const handleRestore = useCallback(async (event: Event) => {
    if (event.seriesId) {
      await restoreSeries(event.seriesId);
    } else {
      await restoreEvent(event.id);
    }
  }, [restoreEvent, restoreSeries]);

  const handleMoveEvent = useCallback(async (eventId: string, newDatetime: Date, originalEvent: Event) => {
    // Save original state for undo
    const originalDatetime = originalEvent.datetime ? new Date(originalEvent.datetime) : null;
    const originalEndTime = originalEvent.endTime;
    const wasException = originalEvent.isException;
    const isRecurring = !!originalEvent.seriesId;

    try {
      // Preserve duration: calculate new endTime as ISO timestamp
      let newEndTimeISO: string | undefined;

      if (originalDatetime && originalEndTime) {
        let oldEndMs: number;

        if (originalEndTime.includes('T')) {
          oldEndMs = new Date(originalEndTime).getTime();
        } else {
          const [h, m] = originalEndTime.split(':').map(Number);
          const endDate = new Date(originalDatetime);
          endDate.setHours(h, m || 0, 0, 0);
          oldEndMs = endDate.getTime();
        }

        const durationMs = oldEndMs - originalDatetime.getTime();
        const newEnd = new Date(newDatetime.getTime() + durationMs);
        newEndTimeISO = newEnd.toISOString();
      }

      const updateData: Partial<Event> = { datetime: newDatetime };
      if (newEndTimeISO) {
        updateData.endTime = newEndTimeISO;
      }

      // For recurring events: mark as exception
      if (isRecurring && !wasException) {
        updateData.isException = true;
      }

      await supabaseEventService.updateEvent(eventId, updateData);

      // Format the toast message
      const dayLabel = newDatetime.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      const timeLabel = newDatetime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      const suffix = isRecurring ? ' · Only this week' : '';

      // Toast with undo button
      toast(
        (t) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>
              Moved to {dayLabel} at {timeLabel}{suffix}
            </span>
            <button
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  // Undo: restore original datetime, endTime, and exception status
                  const undoData: Partial<Event> = {};
                  if (originalDatetime) undoData.datetime = originalDatetime;
                  if (originalEndTime) undoData.endTime = originalEndTime;
                  if (isRecurring && !wasException) undoData.isException = false;
                  await supabaseEventService.updateEvent(eventId, undoData);
                  if (originalEvent.organizerId) {
                    smartListenerService.forceRefreshOrganizerEvents(originalEvent.organizerId);
                  }
                  toast.success('Move undone');
                } catch {
                  toast.error('Failed to undo');
                }
              }}
              style={{
                background: 'none',
                border: '1px solid currentColor',
                borderRadius: '4px',
                padding: '2px 8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '12px',
                color: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              Undo
            </button>
          </div>
        ),
        { duration: 6000, style: { maxWidth: '400px' } }
      );

      if (isRecurring) {
        // Additional hint for recurring events
        toast(
          'To move all occurrences, use Edit series',
          { duration: 4000, icon: '💡', style: { fontSize: '13px' } }
        );
      }

      if (originalEvent.organizerId) {
        smartListenerService.forceRefreshOrganizerEvents(originalEvent.organizerId);
      }
    } catch {
      toast.error('Failed to move event');
    }
  }, []);

  const handleInstagram = useCallback((event: Event) => {
    setInstaEvent(event);
  }, []);

  const handleAddOccurrence = useCallback(async (event: Event) => {
    if (!event.seriesId) return;
    try {
      const seriesData = await locationCache.getSeries(event.seriesId);
      if (seriesData) {
        setAddOccurrenceSeries(seriesData);
        setShowAddOccurrence(true);
      }
    } catch (error) {
      console.error('Error loading series data:', error);
    }
  }, []);

  const handleCloseAddOccurrence = useCallback(() => {
    setShowAddOccurrence(false);
    setAddOccurrenceSeries(null);
  }, []);

  // Show skeleton during initial load
  if (isLoading && events.length === 0) {
    return (
      <div className="planning-view">
        <DayPicker
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          onNextDay={goToNextDay}
          onPreviousDay={goToPreviousDay}
          events={[]}
        />
        <div className="planning-view__skeleton">
          <div className="planning-view__skeleton-card" />
          <div className="planning-view__skeleton-card" />
          <div className="planning-view__skeleton-card" />
        </div>
      </div>
    );
  }

  return (
    <div className={`planning-view planning-view--${variant}`}>
      {variant === 'desktop' ? (
        <div className="planning-view__week-nav">
          <button
            className="planning-view__week-nav-arrow"
            onClick={goToPreviousWeek}
            type="button"
            aria-label="Previous week"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          <span className="planning-view__week-nav-month">
            {(() => {
              const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const startMonth = currentWeek.start.getMonth();
              const endMonth = currentWeek.end.getMonth();
              if (startMonth === endMonth) return MONTHS[startMonth];
              return `${MONTHS[startMonth]} – ${MONTHS[endMonth]}`;
            })()}
          </span>
          <span className="planning-view__week-nav-label">
            Week {currentWeek.weekNumber}
          </span>
          <span className="planning-view__week-nav-count">
            — {totalEventsThisWeek} {totalEventsThisWeek === 1 ? 'event' : 'events'}
          </span>
          <button
            className="planning-view__week-nav-arrow"
            onClick={goToNextWeek}
            type="button"
            aria-label="Next week"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>
      ) : (
        <>
          <DayPicker
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onNextDay={goToNextDay}
            onPreviousDay={goToPreviousDay}
            events={events}
          />
          <div className="planning-view__week-counter">
            {totalEventsThisWeek} {totalEventsThisWeek === 1 ? 'event' : 'events'} this week
          </div>
        </>
      )}

      {variant === 'desktop' ? (
        <PlanningWeekGrid
          weekStart={currentWeek.start}
          eventsByDay={eventsByDay}
          selectedDateKey={selectedDateKey}
          eventMetrics={eventMetrics}
          seriesMetrics={seriesMetrics}
          metricsLoading={metricsLoading}
          onEdit={onEventSelect}
          onDuplicate={handleDuplicate}
          onCancel={handleCancel}
          onRestore={handleRestore}
          onPublish={handlePublish}
          onDelete={handleDelete}
          onInstagram={handleInstagram}
          onAddOccurrence={handleAddOccurrence}
          onCreateEvent={onCreateEvent}
          onCreateEventAt={onCreateEventAt}
          onMoveEvent={handleMoveEvent}
        />
      ) : (
        <PlanningTimeline
          eventsByDay={smartRangeEventsByDay}
          allDayKeys={smartRange.dayKeys}
          selectedDateKey={selectedDateKey}
          eventMetrics={eventMetrics}
          metricsLoading={metricsLoading}
          draftsWithoutDate={draftsWithoutDate}
          onEdit={onEventSelect}
          onDuplicate={handleDuplicate}
          onCancel={handleCancel}
          onRestore={handleRestore}
          onPublish={handlePublish}
          onDelete={handleDelete}
          onInstagram={handleInstagram}
          onAddOccurrence={handleAddOccurrence}
          onCreateEvent={onCreateEvent}
          onSwitchToDrafts={onSwitchToDrafts}
        />
      )}


      {/* Add Occurrence Modal */}
      <AddOccurrenceModal
        isOpen={showAddOccurrence}
        onClose={handleCloseAddOccurrence}
        series={addOccurrenceSeries}
        onSuccess={handleCloseAddOccurrence}
      />

      {/* Instagram Post Generator — lazy loaded */}
      {instaEvent && (
        <Suspense fallback={null}>
          <InstaPostGenerator
            isOpen={!!instaEvent}
            onClose={() => setInstaEvent(null)}
            event={instaEvent}
            organizerName={organizerProfile?.organizer}
            organizerProfile={organizerProfile}
          />
        </Suspense>
      )}
    </div>
  );
};

export default PlanningView;
